import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { useCardStyle } from '@/features/theme/contexts/ThemeContext';

interface PopoverProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
}

export function SimplePopover({
  children,
  trigger,
  open: controlledOpen,
  onOpenChange,
  align = 'center'
}: PopoverProps) {
  const { popoverStyleObject, borderClassName } = useCardStyle();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position when popover opens
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const contentWidth = contentRef.current?.offsetWidth || 500;

      let left = rect.left;
      if (align === 'center') {
        left = rect.left + rect.width / 2 - contentWidth / 2;
      } else if (align === 'end') {
        left = rect.right - contentWidth;
      }

      // Ensure popover doesn't go off screen
      const maxLeft = window.innerWidth - contentWidth - 16;
      left = Math.max(16, Math.min(left, maxLeft));

      setPosition({
        top: rect.bottom + 8,
        left,
      });
    }
  }, [isOpen, align]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const contentWidth = contentRef.current?.offsetWidth || 500;

        let left = rect.left;
        if (align === 'center') {
          left = rect.left + rect.width / 2 - contentWidth / 2;
        } else if (align === 'end') {
          left = rect.right - contentWidth;
        }

        const maxLeft = window.innerWidth - contentWidth - 16;
        left = Math.max(16, Math.min(left, maxLeft));

        setPosition({
          top: rect.bottom + 8,
          left,
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, align]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        triggerRef.current &&
        contentRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !contentRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, setOpen]);

  const handleTriggerClick = () => {
    setOpen(!isOpen);
  };

  const popoverContent = isOpen ? (
    <div
      ref={contentRef}
      className={cn(
        "fixed z-[9999] rounded-md border shadow-2xl overflow-hidden",
        borderClassName,
        "animate-in fade-in-0 zoom-in-95"
      )}
      style={{
        top: position.top,
        left: position.left,
        ...popoverStyleObject,
      }}
    >
      {children}
    </div>
  ) : null;

  return (
    <>
      <div className="relative inline-block">
        <div ref={triggerRef} onClick={handleTriggerClick}>
          {trigger}
        </div>
      </div>
      {popoverContent && createPortal(popoverContent, document.body)}
    </>
  );
}
